import { useUtilisateurService } from '@europrocurement/l2d-domain';
import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useReducer,
} from 'react';
import { castIriToId } from '@europrocurement/l2d-utils';
import {
    UtilisateursApi,
    UtilisateursJsonldUtilisateurRead,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { DataProviderProps, DataState } from './types';

export type User = {
    id: number;
    firstName: string;
    lastName: string;
    keycloakUserIdentifier?: string;
    mail: string;
    deleted: boolean;
    formalist: boolean;
    hasAccessL2DF: boolean;
    service: {
        id: number;
        label: string;
    };
    workGroups: Array<{
        id: number;
        idUserReferent?: number;
        label: string;
    }>;
};

type UserState = DataState & {
    data: Array<User>;
};

const INIT_ITEMS_CTX = 'init_users_ctx';
const ADD_USER = 'add_user';

type Action =
    | { type: 'init_users_ctx'; payload: Array<User> }
    | { type: 'add_user'; payload: User };

export const UsersContext = createContext<UserState | null>(null);
export const UsersDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function usersReducer(state: UserState, action: Action) {
    switch (action.type) {
        case INIT_ITEMS_CTX: {
            return {
                loaded: true,
                data: [...action.payload],
            };
        }
        case ADD_USER: {
            const exists = state.data.some((u) => u.id === action.payload.id);
            if (exists) return state;
            return { ...state, data: [...state.data, action.payload] };
        }
        default: {
            throw new Error(`Unknown action: ${(action as Action).type}`);
        }
    }
}

const getAllUsers = async (
    utilisateurApi: UtilisateursApi,
): Promise<UtilisateursJsonldUtilisateurRead[]> => {
    let buffer: UtilisateursJsonldUtilisateurRead[] = [];

    const doRequest = async (page: number): Promise<UtilisateursJsonldUtilisateurRead[]> => {
        const response = await utilisateurApi.apiUtilisateursGetCollection({
            page,
            itemsPerPage: 100,
        });

        const totalItems = Number(response.data['hydra:totalItems']);
        buffer = [...buffer, ...response.data['hydra:member']];

        if (buffer.length < totalItems) {
            await doRequest(page + 1);
        }

        return Promise.resolve(buffer.flat());
    };

    return await doRequest(1);
};

export const UsersProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(usersReducer, { loaded: false, data: [] });
        const { utilisateurApi } = useUtilisateurService();

        const loadAll = useCallback(async () => {
            let users: UtilisateursJsonldUtilisateurRead[] = [];

            try {
                users = await getAllUsers(utilisateurApi);
            } catch (e) {
                console.error(e);
            }

            dispatch({
                type: INIT_ITEMS_CTX,
                payload: users.map((user) => ({
                    id: user.id as number,
                    firstName: user.prenom as string,
                    lastName: user.nom as string,
                    keycloakUserIdentifier: user?.userIdentifier
                        ? (user?.userIdentifier as string)
                        : undefined,
                    mail: user.mail as string,
                    deleted: user.isDeleted as boolean,
                    formalist: user.isFormalist as boolean,
                    hasAccessL2DF: user.hasAccessL2DF as boolean,
                    service: {
                        id: user?.service?.id as number,
                        label: user?.service?.libelle as string,
                    },
                    workGroups: (user?.groupeTravail || []).map((group) => {
                        let idUserReferent = group?.iduserReferent?.id;

                        if (!idUserReferent && group?.iduserReferent) {
                            idUserReferent = castIriToId(group.iduserReferent as string);
                        }

                        return {
                            id: group.id as number,
                            idUserReferent,
                            label: group.lib as string,
                        };
                    }),
                })),
            });
        }, [utilisateurApi, dispatch]);

        useEffect(() => {
            if (!autoload) {
                return;
            }

            loadAll();
        }, [loadAll, autoload]);

        return (
            <UsersContext.Provider value={state}>
                <UsersDispatchContext.Provider value={dispatch}>
                    {children}
                </UsersDispatchContext.Provider>
            </UsersContext.Provider>
        );
    };

export function useUsersCtx() {
    const state = useContext(UsersContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getUsers = (): Array<User> => state?.data || [];

    const getUserById = (id: number): User | undefined =>
        (state?.data || []).find((user) => user.id === id);

    const getUsersFiltered = (filters: Partial<Record<keyof User, unknown>>): Array<User> =>
        (state?.data || []).filter((item) =>
            Object.entries(filters).every(([key, value]) => item[key as keyof User] === value),
        );

    const getUserFiltered = (filters: Partial<Record<keyof User, unknown>>): User | undefined =>
        getUsersFiltered(filters)[0] || undefined;

    return {
        isDataLoaded,
        getUsers,
        getUserById,
        getUsersFiltered,
        getUserFiltered,
    };
}

export function useUsersCtxDispatch() {
    return useContext(UsersDispatchContext);
}
